import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Chip,
  Grid,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DataObjectIcon from '@mui/icons-material/DataObject';
import { EyeExamTemplateService } from '../../service/eyeExamTemplateService';
import { toast } from 'react-toastify';
import { useAuth } from '../../hooks/useAuth';
import { Colors } from '../../styles/theme';
import TemplateDataEditor from './TemplateDataEditor';

export default function EyeExamTemplatesPage() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [dataEditorOpen, setDataEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [editingData, setEditingData] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await EyeExamTemplateService.list(user?.id, true);
      setTemplates(data || []);
    } catch (error) {
      toast.error('Failed to load templates');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setEditing(null);
    setName('');
    setDescription('');
    setIsPublic(false);
  };

  const openCreate = () => {
    resetForm();
    setFormOpen(true);
  };

  const openEdit = (tpl) => {
    setEditing(tpl);
    setName(tpl.name || '');
    setDescription(tpl.description || '');
    setIsPublic((tpl.scope || 'private') === 'public');
    setFormOpen(true);
  };

  const openView = async (tpl) => {
    try {
      const result = await EyeExamTemplateService.getById(tpl.id);
      setViewing(result);
      setViewOpen(true);
    } catch (error) {
      toast.error('Failed to load template details');
      console.error(error);
    }
  };

  const openDataEditor = async (tpl) => {
    try {
      const result = await EyeExamTemplateService.getById(tpl.id);
      setEditingData({ ...tpl, payload: result.payload || {} });
      setDataEditorOpen(true);
    } catch (error) {
      toast.error('Failed to load template data');
      console.error(error);
    }
  };

  const saveTemplateData = async (payload) => {
    try {
      await EyeExamTemplateService.update(editingData.id, {
        practitionerId: editingData.practitionerId,
        name: editingData.name,
        description: editingData.description,
        scope: editingData.scope,
        payload
      });
      toast.success('Template data updated successfully');
      setDataEditorOpen(false);
      setEditingData(null);
      await load();
    } catch (error) {
      toast.error('Failed to save template data');
      console.error(error);
    }
  };

  const saveTemplate = async () => {
    try {
      const record = {
        practitionerId: user?.id || null,
        name: name.trim() || 'Untitled',
        description: description || '',
        scope: isPublic ? 'public' : 'private',
        payload: {} // Empty payload for now - can be populated when creating from customer exam
      };

      if (editing) {
        await EyeExamTemplateService.update(editing.id, record);
        toast.success('Template updated successfully');
      } else {
        await EyeExamTemplateService.create(record);
        toast.success('Template created successfully');
      }

      setFormOpen(false);
      await load();
    } catch (error) {
      toast.error('Failed to save template');
      console.error(error);
    }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;

    try {
      await EyeExamTemplateService.delete(tpl.id);
      toast.success('Template deleted successfully');
      await load();
    } catch (error) {
      toast.error('Failed to delete template');
      console.error(error);
    }
  };

  const canEditTemplate = (tpl) => {
    // Can edit if it's my template or I'm the creator
    return tpl.practitionerId === user?.id || tpl.scope === 'private';
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Eye Exam Templates</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
        >
          New Template
        </Button>
      </Box>

      {loading && <Typography>Loading...</Typography>}

      {!loading && templates.length === 0 && (
        <Alert severity="info">
          No templates found. Create your first template to get started.
        </Alert>
      )}

      <Grid container spacing={2}>
        {templates.map((tpl) => (
          <Grid item xs={12} sm={6} md={4} key={tpl.id}>
            <Card elevation={2}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                  <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                    {tpl.name}
                  </Typography>
                  <Chip
                    label={tpl.scope === 'public' ? 'Public' : 'Private'}
                    size="small"
                    color={tpl.scope === 'public' ? 'primary' : 'default'}
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                  {tpl.description || 'No description'}
                </Typography>

                {tpl.practitioner && (
                  <Typography variant="caption" color="text.secondary">
                    Created by: {tpl.practitioner.fullName || `${tpl.practitioner.firstName} ${tpl.practitioner.lastName}`}
                  </Typography>
                )}
              </CardContent>

              <CardActions sx={{ justifyContent: 'space-between', px: 2 }}>
                <Box>
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => openView(tpl)}>
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  {canEditTemplate(tpl) && (
                    <Tooltip title="Edit Exam Data">
                      <IconButton size="small" color="primary" onClick={() => openDataEditor(tpl)}>
                        <DataObjectIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Box>
                  {canEditTemplate(tpl) && (
                    <>
                      <Tooltip title="Edit Details">
                        <IconButton size="small" onClick={() => openEdit(tpl)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => deleteTemplate(tpl)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Create/Edit Form Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Edit Template' : 'New Template'}</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Template Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
              autoFocus
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    {isPublic ? 'Public Template' : 'Private Template'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {isPublic
                      ? 'Visible to all practitioners in your organization'
                      : 'Only visible to you'}
                  </Typography>
                </Box>
              }
            />
            <Alert severity="info" sx={{ mt: 1 }}>
              Templates created here start with empty exam data. You can populate them when performing an actual eye exam on a customer.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveTemplate} disabled={!name.trim()}>
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Template Dialog */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Template Details</DialogTitle>
        <DialogContent dividers>
          {viewing && (
            <Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                <Typography variant="body1">{viewing.template?.name}</Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                <Typography variant="body1">{viewing.template?.description || 'No description'}</Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">Scope</Typography>
                <Chip
                  label={viewing.template?.scope === 'public' ? 'Public' : 'Private'}
                  size="small"
                  color={viewing.template?.scope === 'public' ? 'primary' : 'default'}
                />
              </Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">Payload Data</Typography>
                <Box
                  sx={{
                    backgroundColor: Colors.light_gray,
                    p: 2,
                    borderRadius: 1,
                    maxHeight: 300,
                    overflow: 'auto'
                  }}
                >
                  <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                    {JSON.stringify(viewing.payload || {}, null, 2)}
                  </pre>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Template Data Editor */}
      <TemplateDataEditor
        open={dataEditorOpen}
        onClose={() => {
          setDataEditorOpen(false);
          setEditingData(null);
        }}
        template={editingData}
        onSave={saveTemplateData}
      />
    </Box>
  );
}
